import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import ErrorIcon from '@mui/icons-material/Error';
import RuleScreen from '../../shared/RuleScreen';
import SundargarhFilterBar from '../../shared/SundargarhFilterBar';
import StatTile from '../../shared/StatTile';
import DataTable from '../../shared/DataTable';
import Pagination, { normalizePagination } from '../../shared/Pagination';
import DetailDrawer from '../../shared/DetailDrawer';
import { buildParams } from '../../shared/apiParams';

const API_URL = '/api/survey'; // Express backend (services/sundargarhData.js), same live-Kobo data source as the old Vercel functions
const EMPTY = { ulbs: [], wards: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'ulb', label: 'By ULB', col: 'ULB' },
  { key: 'ward', label: 'By Ward', col: 'Ward' },
];

const pctCell = (b, highlight) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: highlight ? 'var(--danger)' : 'var(--muted)', fontWeight: highlight ? 700 : 400, marginLeft: 4 }}>
      ({b?.pct || 0}%)
    </span>
  </>
);

export default function SundargarhSchoolCount({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, filter: 'all', records: [], loading: false });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/sundargarh/rules/school-count/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching Sundargarh school-count data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalSurveys = summary?.totalSurveys ?? rows.reduce((s, r) => s + (Number(r.totalSurveys) || 0), 0);

  const tiles = (
    <>
      <StatTile label="Total Surveys" value={totalSurveys} icon={GroupsIcon} accent="var(--primary)" />
      <StatTile
        label="Count Mismatch"
        value={`${summary?.mismatch?.pct ?? 0}%`}
        icon={ErrorIcon}
        accent={(summary?.mismatch?.pct ?? 0) >= 25 ? 'var(--danger)' : 'var(--warning)'}
        sub={`${summary?.mismatch?.count ?? 0} of ${totalSurveys} surveys`}
      />
    </>
  );

  const openDrilldown = async (row, filter = 'all') => {
    setDrawer({ open: true, row, filter, records: [], loading: true });
    try {
      const params = buildParams(applied, { extra: { groupBy: activeTab, groupValue: row.name, filter, limit: 100 } });
      const res = await axios.get(`${API_URL}/sundargarh/rules/school-count/details?${params}`);
      setDrawer({ open: true, row, filter, records: res.data?.data || [], loading: false });
    } catch (err) {
      console.error('Error fetching school-count detail records:', err);
      setDrawer({ open: true, row, filter, records: [], loading: false });
    }
  };

  const mismatchCell = (row) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, 'mismatch'); }}>
      {pctCell(row.mismatch, true)}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center', render: (r) => <span className="cell-strong">{r.totalSurveys || 0}</span> },
    { key: 'mismatch', label: 'Receiving > Eligible', align: 'center', render: (r) => mismatchCell(r) },
  ];

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    { key: 'household_id', label: 'Household ID', render: (r) => r.household_id || '—' },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'autoCount', label: 'Eligible', align: 'center', render: (r) => r.autoCount ?? '—' },
    { key: 'childrenInSchool', label: 'Receiving Scholarship', align: 'center', render: (r) => r.childrenInSchool ?? '—' },
    { key: 'ulb', label: 'ULB', render: (r) => r.ulb || '—' },
    { key: 'ward', label: 'Ward', render: (r) => r.ward || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
  ];

  return (
    <RuleScreen
      title={rule?.title || 'Children-in-School Count Wrong'}
      subtitle="Flags surveys where the scholarship-receiving children count exceeds the eligible children count (adapted from the internal auto-calculated-count check, which this form has no equivalent field for)."
      icon={SchoolIcon}
      backTo={rule?.category ? `/sundargarh-category/${rule.category}` : undefined}
      backLabel="Back"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button key={t.key} className={`tab-btn${activeTab === t.key ? ' active' : ''}`} onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}>
                {t.label}
              </button>
            ))}
          </div>
          <SundargarhFilterBar onApply={(v) => { setApplied(v); setPage(1); }} onClear={() => { setApplied(EMPTY); setPage(1); }} loading={loading} />
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Children-in-School Count by {tab.label.replace('By ', '')}</h3>
        <Pagination pagination={pagination} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} unitLabel="groups" shown={rows.length} />
        <DataTable columns={columns} rows={rows} loading={loading} emptyMessage='No data found. Adjust filters and click "Apply Filters".' onRowClick={(r) => openDrilldown(r, 'all')} getRowKey={(r, i) => `${r.name}-${i}`} />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${drawer.filter === 'mismatch' ? 'Count Mismatch' : 'All Surveys'}` : ''}
        sections={
          drawer.row
            ? [{ title: 'Breakdown', items: [
                { label: 'Total Surveys', value: drawer.row.totalSurveys || 0 },
                { label: 'Count Mismatch', value: `${drawer.row.mismatch?.count || 0} (${drawer.row.mismatch?.pct || 0}%)` },
              ] }]
            : []
        }
      >
        <div className="detail-section">
          <h4>Surveys</h4>
          <DataTable columns={drawerRecordColumns} rows={drawer.records} loading={drawer.loading} emptyMessage="No surveys found." getRowKey={(r, i) => r.uuid || i} />
        </div>
      </DetailDrawer>
    </RuleScreen>
  );
}
